import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import classNames from 'classnames';

import './QuickBet.scss';
import LottoContext from '../../NumbersBetting/Context/Lotto.context';
import RacingDetailContext from '../../HorseBetting/Context/RacingDetail.context';
import VirtualKeyboardContext from '../../VirtualKeyboard/Context/VirtualKeyboard.context';
import { RootState } from '../../../configs/store';
import { Bet } from '../../../interfaces/BetslipState';
import NumberBet from '../NumberBet/NumberBet';
import RacingBet from '../RacingBet/RacingBet';
import ShowCombinations from '../ShowCombinations/ShowCombinations';
import DoubleStakes from '../DoubleStakes/DoubleStakes';
import CombinationsModal from '../../NumbersBetting/LottoSummary/CombinationsModal/CombinationsModal';
import Icon from '../../Icon/Icon';
import VirtualKeyboard from '../../VirtualKeyboard/VirtualKeyboard';
import { checkIfCrossEvent, combinationsValues, orderPosition } from 'src/utils/racingCombinations';
import AppModeContext from 'src/containers/AppMode/AppMode.context';
import { generateCalculationsPayload } from 'src/utils/lottoHelper';

interface QuickBetProps {
  tournamentName: string,
  country: string,
  isRacingBet?: boolean,
  icon?: string
}

export default function QuickBet({ tournamentName, country, isRacingBet=false, icon }: QuickBetProps) {

  const { singleNumberEvent: { numberInfos: {  bonus } } } = useSelector((state: RootState) => state.lotto);
  const selectedSlip  = useSelector((state: RootState) => state.betslip.slip);
  const { stakingRules }  = useSelector((state: RootState) => state.content);
  const activeWallet = useSelector((state: RootState) => state.auth.localWalletSelected);
  const { betResult } = useSelector((state: RootState) => state.betslip);

  const [showModal, setShowModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [lastStake, setLastStake] = useState('totalStake'); //This will allow us to add with the buttons on the last changed input
  const [height, setHeight] = useState(0);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [selectedInput, setSelectedInput] = useState(null);
  const [numberIsScrollable, setNumberIsScrollable] = useState(false);
  const [numberBallHeight, setNumberBallHeight] = useState<number>(0); // max height of numberBall before scroll
  const [quickBetValid, setQuickBetValid] = useState(true);
  const [stakes, setStakes] = useState({ totalStake:10, singleStake:0 });
  const [error, setError] = useState<string>(null);
  const [activeContext, setActiveContext] =useState(null);
  const [maximumReturn, setMaximumReturn] = useState(null);

  const containerHeight = useRef(null);
  const combinationsHeight = useRef(null);
  const quickBetRef = useRef(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const context = {
    showKeyboard,
    setShowKeyboard,
    selectedInput,
    setSelectedInput
  };

  const racingContext = useContext(RacingDetailContext);
  const lottoContext = useContext(LottoContext);

  useEffect(() => {
    setActiveContext(isRacingBet ? racingContext : lottoContext);
  }, []);

  const { RETAIL_MODE } = useContext(AppModeContext);

  const events = !isRacingBet ? _.filter(activeContext?.selectedEvents, { selected: true }) : [activeContext?.event];
  const stakeButtons = [1, 2, 5, 10, 20, 50, 100]; //Value of the buttons
  const bet:Bet = {
    eventCode: !isRacingBet ? events[0]?.code : events[0]?.eventCode,
    providerCode: 1,
    odds: !isRacingBet ? activeContext?.selectedMarket.outcomes[0].value : 'TP',
    marketCode: !isRacingBet ? activeContext?.selectedMarket.marketCode : activeContext?.selectedMarket.code,
    tournamentName,
    datetime: (!isRacingBet ? events[0]?.scheduled : events[0]?.eventStart) * 1000,
    eventName: tournamentName,
    sport: !isRacingBet ? 'numbers' :'racing',
    outcome: !isRacingBet ? activeContext?.selectedMarket.outcomes[0].name : t(`racingDetail.${ activeContext?.selectedSelectionType }`),
    type:!isRacingBet ? 'numbers' : 'racing',
    permutationsCount: activeContext?.combinations?.prediction.permutationsCount,
    marketName: !isRacingBet ? '' : activeContext?.selectedMarket.name.toLowerCase(),
    marketDetails: racingContext.selectedMarket,
    ...(racingContext.selectedMarket.isCrossEventMarket && { crossEvent: 'cross_event' }),
    country: country,
    selectedNumbers: _.map(activeContext?.selectedNumbers),
    orderedPosition: racingContext ?
      orderPosition(racingContext?.selectedSelectionType, racingContext?.selectedMarket, racingContext?.selectedHorsePosition) :
      null,
    horsePosition: activeContext?.selectedHorsePosition,
    productType: !isRacingBet? 4 : 2,
    selectionType:  activeContext?.selectedSelectionType,
    stake: stakes.totalStake,
    sameBallSet: !isRacingBet ? bonus?.sameBallset : true,
    repetitionsCount: isRacingBet && checkIfCrossEvent(activeContext?.selectedMarket) ? 2 : events.length,
    oddsType: isRacingBet && activeContext?.selectedMarket.oddsType,
    horseType: isRacingBet && activeContext?.selectedMarket.oddsType === 2 ? 'TP' : 'FO',
    raceNo: activeContext?.event?.raceNo || '',
    racingType: icon || ''
  };
  let filteredBonus;

  if (activeContext?.selectedMarket?.typeCode === 800){
    filteredBonus = _.filter(activeContext?.selectedNumbers, { type: 'bonus' });
  }

  const getPermutations = async (name, value) => {
    if (!isRacingBet && activeContext?.selectedMarket){
      let numbers = _.map(activeContext?.selectedNumbers, (number) => number.number);

      if (activeContext?.selectedMarket.typeCode === 800 && filteredBonus.length > 0){
        numbers = [...numbers, ..._.map(filteredBonus, (number) => number.number)];
      }

      const bankers = _.map(_.filter(activeContext?.selectedNumbers, { isBanker:true }), (banker) => banker.number);
      const specifiers = activeContext?.selectedMarket?.specifiers;

      const numbersToPredictCount = activeContext?.selectedMarket?.specifiers && specifiers && specifiers?.length > 1 ?
        +specifiers[0].value + +specifiers[1]?.value : +(specifiers[0]?.value);

      const payload = generateCalculationsPayload(
        numbers,
        bankers,
        activeContext?.selectedMarket,
        bet.eventCode,
        name === 'totalStake' ?  value : value * numbersToPredictCount,
        numbersToPredictCount,
        filteredBonus
      );

      const data = await dispatch.lotto.permutations(payload);
      if (data){
        activeContext.setCombinations(data.bets[0]);

        setStakes({ totalStake: data.bets[0].prediction.totalStake, singleStake: data.bets[0].prediction.singleStake });
        setMaximumReturn(data.bets[0].prediction.maximumReturn);
      }
    }
  };

  const onStakeChange = (name: string, value: string) => {
    const val = parseFloat(value);
    setLastStake(name);
    if (!isRacingBet){
      getPermutations(name, val);

    } else {
      if (name === 'totalStake'){
        setStakes({ totalStake:val, singleStake: val/activeContext?.combinations.permutationsCount });
      } else {
        setStakes({ singleStake:val, totalStake: val*activeContext?.combinations.permutationsCount });
      }
    }
  };

  useEffect(() => {
    const rules = !isRacingBet? stakingRules.lotto.single : stakingRules.racing.tote;
    onStakeChange('singleStake', rules.default.toString());
  }, [activeContext]);

  const handleCloseWarning = (e) => {
    e.preventDefault();
    setShowWarning(false);
  };

  useEffect(() => {
    // This will allow table combinations to have the same height that numberBet so it does not jump
    setTimeout(() => {
      const heightContainer = containerHeight.current?.offsetHeight;
      const heightCombinations  = combinationsHeight.current?.offsetHeight;
      if (heightContainer>0 && heightCombinations > 0){
        setHeight(heightContainer+heightCombinations);
      }
    }, 100);
  }, [containerHeight.current, combinationsHeight.current]);

  useEffect(() => {
    // Check if the numbers should scroll
    const height = quickBetRef.current?.scrollHeight - (window.innerHeight - 16);
    if (height > 0){
      setNumberIsScrollable(true);
      setNumberBallHeight(containerHeight.current.offsetHeight / 2 - height); //calculate max height of the ball to scroll
    }
  }, [quickBetRef.current]);

  useEffect(() => {
    document.body.classList.add('disable-scroll');

    return () => {
      document.body.classList.remove('disable-scroll');
    };
  }, []);

  useEffect(() => {
    const rules = !isRacingBet? stakingRules.lotto.single : stakingRules.racing.tote;
    if (stakes.totalStake < rules.minimum) {
      setQuickBetValid(false);
      setError(t('betslip.minimumError'));
    } else if (stakes.totalStake > rules.maximum){
      setError(t('betslip.maximumError'));
      setQuickBetValid(false);
    } else {
      setError(null);
      setQuickBetValid(true);
    }
  }, [stakes]);

  const handleStakeButton = (e) => {
    e.preventDefault();
    let value = parseFloat(e.target.value);
    value =  stakes[lastStake] + value;
    if (value) {
      if (!isRacingBet){
        setStakes({ ...stakes, [lastStake]: value });
      } else {
        if (lastStake === 'totalStake'){
          setStakes({ totalStake:value, singleStake: value/activeContext.combinations.prediction.permutationsCount });
        } else {
          setStakes({ singleStake:value, totalStake: value*activeContext.combinations.prediction.permutationsCount });
        }
      }
    } else {
      setStakes({ totalStake: 0, singleStake: 0 });
      value = 0;
    }

    if (!isRacingBet) getPermutations(lastStake, value);
  };

  const getNumbersBetPayload = () => {
    const bets = [];
    for (const event of events){
      const newBet = {
        eventCode: event.code,
        eventName: tournamentName,
        providerCode: 1,
        odds: !isRacingBet ? activeContext?.selectedMarket.outcomes[0].value : 0,
        marketCode: !isRacingBet ? activeContext.selectedMarket.code : activeContext.selectedMarket.marketCode,
        marketName: activeContext.selectedMarket.name,
        marketOutcomeCode: !isRacingBet ? activeContext.selectedMarket.outcomes[0].code : activeContext.selectedMarket.marketCode,
        tournamentName,
        datetime: events[0].scheduled * 1000,
        sport: activeContext.sportType,
        type:bet.type,
        country: country,
        selectedNumbers: !isRacingBet ? _.map((activeContext.selectedNumbers)) : activeContext.selectedHorsePosition,
        permutationsCount: activeContext.combinations.permutationsCount,
        selectedEvents: bet.repetitionsCount,
        stake: stakes.totalStake,
        productType: bet.productType,
        numbersToPredictCount: activeContext.combinations.numbersToPredictCount,
        repetitionsCount: bet.repetitionsCount,
        sameBallSet:  bonus?.sameBallset
      };
      bets.push(newBet);
    }
    return bets;
  };


  const addToBetSlip = async () => {
    if (betResult){
      await dispatch.betslip.clearBetResult();
    }
    if (selectedSlip !== 0 && !showWarning && activeContext.combinations.prediction.permutationsCount > 1){
      setShowWarning(true);
    } else {
      if (activeContext.combinations.prediction.permutationsCount > 1){
        await dispatch.betslip.changeSlip(0);
      }

      if (!isRacingBet){
        const bets = getNumbersBetPayload();
        if (bet.permutationsCount> 1 || bet.repetitionsCount > 1){
          await dispatch.betslip.changeStakeSelection('differentStake');
        }
        await dispatch.betslip.addBets(bets);
        activeContext.setselectedNumbers([]); //Numbers needs to be reset after adding to betslip.
      } else {
        const prediction = combinationsValues(bet.orderedPosition, bet.selectionType, racingContext.selectedMarket, bet.horsePosition);

        bet.marketOutcomeCode = racingContext.selectedMarket.outcomes[0].code;

        if (bet.permutationsCount > 1 || bet.repetitionsCount > 1){
          await dispatch.betslip.changeStakeSelection('differentStake');
        }
        await dispatch.betslip.addBet({ ...bet, prediction });
        const numberOfPositions = activeContext.selectedMarket.isCrossEventMarket ?
          activeContext.selectedMarket.predictionCount :
          activeContext.selectedMarket.predictionCount +1;
        activeContext.setSelectedHorsePosition([...Array(numberOfPositions)].map(() => Array(0)));
      }
      activeContext.setQuickBet(false);
      setStakes({ totalStake:10, singleStake:0 }); // Reset Stake
    }

  };

  const handleClose = (e) => {
    e.preventDefault();
    activeContext.setQuickBet(false);
  };

  const fixStakes = (e) => {
    e.preventDefault();
    const rules = !isRacingBet? stakingRules.lotto.single : stakingRules.racing.tote;
    if (stakes.totalStake < rules.minimum) {
      setStakes({ singleStake: rules.minimum/activeContext?.combinations.permutationsCount || 0, totalStake: rules.minimum });
    } else {
      setStakes({ singleStake: rules.maximum/activeContext?.combinations.permutationsCount || 0, totalStake: rules.maximum });
    }
  };

  const handlePlaceBet = (e) => {
    e.preventDefault();

    if (_.isEmpty(activeWallet)){
      dispatch.content.modalType('#login');
    } else {
      if (isRacingBet){
        const prediction = combinationsValues(bet.orderedPosition, bet.selectionType, racingContext.selectedMarket, bet.horsePosition);

        bet.marketOutcomeCode = racingContext.selectedMarket.outcomes[0].code;
        dispatch.betslip.placeBet({ isRetail: RETAIL_MODE, bet: [{ ...bet, prediction }] });
      } else {
        const bets = getNumbersBetPayload();
        dispatch.betslip.placeBet({ isRetail: RETAIL_MODE, bet: bets });
        activeContext.setselectedNumbers([]);
      }
      activeContext.setQuickBet(false);
    }
  };

  return (
    <VirtualKeyboardContext.Provider value={ context }>
      {
        activeContext !== null && (
          <div className="quick-bet">
            <div className="quick-bet__expanded" onClick={ handleClose }></div>
            <div className="quick-bet__info" ref={ quickBetRef }>
              <div className="div quick-bet__title">
                { t('numbers.quickBet') }
                <Icon icon="close" className="slip-form__close-warning" onClick={ handleClose } />
              </div>
              {
                !showModal && (
                  <div ref={ containerHeight }>
                    {
                      !isRacingBet &&(
                        <NumberBet
                          key={ bet.eventName + bet.marketName + bet.outcome }
                          bet={ bet }
                          hideProfit
                        >
                          <NumberBet.QuickBet
                            scrollable={ numberIsScrollable }
                            maxHeight={ numberBallHeight }
                          />
                        </NumberBet>
                      )
                    }
                    {
                      isRacingBet &&
                        (
                          <RacingBet
                            bet={ bet }
                            isQuickBet={ true }
                            icon={ icon }
                            showBanker={ false }
                            key={ bet.eventName + bet.marketName + bet.outcome }
                            rule={ bet.oddsType === 2 ? stakingRules.racing.tote : stakingRules.racing.fixedOdds }
                            hideProfit
                          />
                        )
                    }
                  </div>)
              }

              {
                showModal && <div>
                  <CombinationsModal
                    title={ t('numbers.combinations') }
                    permutations={ isRacingBet ? racingContext.combinations.permutations : activeContext?.combinations.permutations }
                    stake={ stakes?.singleStake }
                    odds={ activeContext?.combinations.odds }
                    permutations2={ activeContext?.combinations.prediction?.permutations2 }
                    profit={  maximumReturn }
                    onClick={ () => setShowModal(!showModal) }
                    height={ height }
                    isQuickBet={ true }
                    isRacing={ isRacingBet }
                  />
                </div>
              }
              {
                !showModal && (
                  <div ref={ combinationsHeight }>
                    <ShowCombinations
                      repetitionsCount={ events.length }
                      isQuickBet={ true }
                      totalCombinations={
                        isRacingBet?
                          racingContext.combinations?.permutationsCount : activeContext?.combinations?.permutationsCount
                      }
                      onClick={ () => setShowModal(true) }
                      isNumberBet={ !isRacingBet }
                    />
                  </div>
                )
              }
              <div className="stakes">
                <DoubleStakes
                  isQuickBet={ true }
                  stakes={ stakes }
                  handleChange={ onStakeChange }
                  selectedStake={ lastStake }
                  changeSelectedStake={ setLastStake }
                  handleClose={ handleCloseWarning }
                  showWarning ={ showWarning }
                  selectedDraws={ events.length }
                  quickBetValid={ quickBetValid }
                />
              </div>
              <div className="quick-bet__stake-buttons">
                <VirtualKeyboard>
                  <div className="quick-bet__stake-buttons">
                    {
                      _.map(stakeButtons, (value, index) => {
                        return (
                          <button className="quick-bet__outlined-btn" onClick={ handleStakeButton } key={ index } value={ value }>
                            { `+${ value }` }
                          </button>
                        );
                      })
                    }
                    <button className="quick-bet__outlined-btn" onClick={ handleStakeButton }>Clear</button>
                  </div>
                </VirtualKeyboard>
                {
                  quickBetValid && <button
                    className="quick-bet__place-bet"
                    onClick={ handlePlaceBet }
                    disabled={ !quickBetValid }
                  >
                    <span>{
                      t('betslip.placeBetBtn', {
                        value: !isRacingBet ? (stakes.totalStake * 1).toFixed(2) : stakes.totalStake.toFixed(2) })
                    }</span>
                    {
                      !isRacingBet && (<span>
                        {
                          t('betslip.potentialReturnBtn', {
                            return: maximumReturn && (maximumReturn).toFixed(2)
                          })
                        }
                      </span>)
                    }
                  </button>
                }
                {
                  !quickBetValid && (
                    <div className="quick-bet__minimum-stakes">
                      <div className="quick-bet__wrapper--text">
                        <Icon icon="warning" className="quick-bet__warning-icon" />
                        { error }
                      </div>
                      <div className="quick-bet__change-stakes" onClick={ fixStakes }>{ t('betslip.accept') }</div>
                    </div>
                  )
                }
                <button
                  className={
                    classNames({
                      'quick-bet__add-betslip':true,
                      'quick-bet__add-betslip--disabled': !quickBetValid
                    })
                  }
                  disabled={ !quickBetValid }
                  onClick={ addToBetSlip }
                >
                  { !showWarning ? t('numbers.addToBetslip') : t('betConfirmation.acceptAndAdd') }
                </button>
              </div>
            </div>
          </div>)
      }
    </VirtualKeyboardContext.Provider>
  );
}
